/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { MainNavigationStyles } from "@insite/content-library/Widgets/Header/MainNavigation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import { CustomAppState, xHeaderActions } from "@stoneage/App/Store/Reducers";
import { trackAsImpersonation, trackGaNavBarClickEvent } from "@stoneage/App/Utilities/DataLayer";
import Tooltip from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { MappedLink } from "@stoneage/Overrides/Widgets/Header/MainNavigation";
import { styledScrollbar } from "@stoneage/XDL/Utilities/SharedStyles/sharedStyles";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
    isProductsMenu: boolean;
    isWideMenu: boolean;
    numberOfRowsPerColumn: number;
    wideMenuTheme?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type;

    return {
        toggleHeaderColor: state.xHeader.toggleHeaderColor,
        isSticky: state.xHeader.isSticky,
        isHomePage: pageType === "HomePage", // HomePage
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    doToggleHeaderColor: (open: boolean) => (dispatch: Dispatch<xHeaderActions>) => {
        dispatch({ type: "xHeader/doToggleHeaderColor", open });
    },
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

class MainNavigationItem extends React.Component<Props> {
    element = React.createRef<HTMLElement>();
    tippyElement = undefined;

    componentDidMount() {
        setTimeout(() => {
            if (window) {
                window.dispatchEvent(new Event("resize"));
            }
        }, 200);
    }

    componentDidUpdate(prevProps: Readonly<Props>) {}

    closePopover = () => {
        (this.tippyElement as any)?.hide();
    };

    render() {
        const { styles, link, index, isProductsMenu, isWideMenu, wideMenuTheme, impersonation } = this.props;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isMega = hasChildren && link.linkType === "MegaMenu";
        const toggleTheme = this.props.isSticky || !this.props.isHomePage || this.props.toggleHeaderColor;

        let menuLink = (
            <Link
                className={toggleTheme ? "menu-item toggle" : "menu-item"}
                typographyProps={styles.menuItemTypography}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
                aria-label="main-navigation-link"
                onClick={() => {
                    // XNG-Change: track GA4
                    trackGaNavBarClickEvent(link.title, impersonation);
                }}
            >
                {link.title} {isMega && downIcon}
            </Link>
        );
        if (link.url === "") {
            menuLink = (
                <Link
                    className={toggleTheme ? "menu-item toggle" : "menu-item"}
                    typographyProps={styles.menuItemTypography}
                    {...styles.menuItem}
                    aria-label="main-navigation-menu"
                    role="menu"
                >
                    {link.title} {isMega && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        const { numberOfRowsPerColumn } = this.props;

        if (isMega) {
            menuItem = (
                <Tooltip
                    trigger="mouseenter focus"
                    hideOnClick={true}
                    onOpen={({ instance }: any) => {
                        this.tippyElement = instance;
                    }}
                    onClose={({ instance }: any) => {
                        this.tippyElement = undefined;
                    }}
                    placement="bottom-end"
                    triggerAltText="Expand Menu"
                    triggerComponent={menuLink}
                    delay={[50, 70]}
                    offset={[13, 12]}
                    cssOverrides={{
                        tooltipBody: css``,
                        tooltipContainer: css`
                            > .tippy-arrow {
                                display: none;
                            }
                            max-width: initial;
                            box-shadow: 0 2px 2px 0 rgb(0 0 0 / 33%);
                            border-radius: 0;
                            min-width: 194px;
                            padding: 17px 0 0 17px;
                            height: auto;
                            overflow-y: auto;
                            ${() => {
                                if (this.props.numberOfRowsPerColumn) {
                                    return `
                                    max-height: ${
                                        numberOfRowsPerColumn * 32 + 84
                                    }px;                                        
                                    `;
                                }
                                return `
                                max-height: 308px;

                                `;
                            }}
                            ${styledScrollbar(8)};
                            ${() => {
                                const logoLinksSearchElementPosition = document
                                    .getElementsByClassName("header_logo-links-search")[0]
                                    .getBoundingClientRect();

                                if (typeof document === "undefined" && document === null) {
                                    return ``;
                                }
                                if (this.props.isWideMenu) {
                                    return `
                                        width: ${logoLinksSearchElementPosition.width}px;
                                    `;
                                }
                                return ``;
                            }}
                        `,
                    }}
                    passAsChildren={true}
                >
                    <StyledWrapper
                        className="mega-menu-grid-container"
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        {...({ numberOfRowsPerColumn: this.props.numberOfRowsPerColumn } as any)}
                        as="ul"
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => (
                                <StyledWrapper
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    className={`mega-menu-grid-item ${
                                        child.title &&
                                        child.title.toLowerCase() === "products" &&
                                        "products-content-container"
                                    } ${!isWideMenu && "one-column"} ${isWideMenu && "multiple-columns"}`}
                                    {...styles.megaMenuGridItem}
                                    as="li"
                                >
                                    {!isProductsMenu && (
                                        <Link
                                            className="mega-menu-heading-link"
                                            {...styles.megaMenuHeading}
                                            href={child.url}
                                            onClick={() => {
                                                // XNG-Change: track GA4
                                                trackGaNavBarClickEvent(child.title, impersonation);
                                                this.closePopover();
                                            }}
                                            target={child.openInNewWindow ? "_blank" : ""}
                                        >
                                            {child.title}
                                        </Link>
                                    )}
                                    {child.children && (
                                        <StyledWrapper
                                            data-test-selector={`linksFor_${child.title}`}
                                            className="level2-ul"
                                            as="ul"
                                        >
                                            {child.children
                                                .filter(grandChild => {
                                                    if (grandChild.excludeFromNavigation) {
                                                        return false;
                                                    }
                                                    if (
                                                        grandChild.type === "ChangeCustomerPage" &&
                                                        !this.props.displayChangeCustomerLink
                                                    ) {
                                                        return false;
                                                    }
                                                    return true;
                                                })
                                                .map((grandChild, index) => (
                                                    <StyledWrapper
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={index}
                                                        as="li"
                                                        className="level2-li"
                                                        {...({ link } as any)}
                                                        css={css`
                                                            ${({ link }: any) => {
                                                                if (
                                                                    link &&
                                                                    grandChild.children &&
                                                                    grandChild.children.length > 0
                                                                ) {
                                                                    if (
                                                                        grandChild.children.length <
                                                                        numberOfRowsPerColumn
                                                                    ) {
                                                                        return `width: 25%`;
                                                                    }

                                                                    return `
                                                                    width: calc((100% / 4) * (${
                                                                        Math.ceil(
                                                                            grandChild.children.length /
                                                                                numberOfRowsPerColumn,
                                                                        ) || 1
                                                                    }));
                                                                `;
                                                                }
                                                                return `
                                                                    width: 25%;
                                                                `;
                                                            }}
                                                        `}
                                                    >
                                                        <StyledWrapper className="level2-li-link-wrapper">
                                                            <Link
                                                                className="mega-menu-link"
                                                                {...styles.megaMenuLink}
                                                                onClick={() => {
                                                                    // XNG-Change: track GA4
                                                                    trackGaNavBarClickEvent(
                                                                        grandChild.title,
                                                                        impersonation,
                                                                    );
                                                                    this.closePopover();
                                                                }}
                                                                href={grandChild.url}
                                                                data-test-selector={grandChild.title}
                                                                target={grandChild.openInNewWindow ? "_blank" : ""}
                                                            >
                                                                {grandChild.title}
                                                            </Link>
                                                        </StyledWrapper>
                                                        {/* SAINC-1522: https://xngage.atlassian.net/browse/SAINC-1522 */}
                                                        {grandChild.children && wideMenuTheme && (
                                                            <ul
                                                                data-test-selector={`linksFor_${child.title}`}
                                                                className="level3-ul"
                                                            >
                                                                {grandChild.children
                                                                    .filter(grandGrandChild => {
                                                                        if (grandGrandChild.excludeFromNavigation) {
                                                                            return false;
                                                                        }
                                                                        if (
                                                                            grandGrandChild.type ===
                                                                                "ChangeCustomerPage" &&
                                                                            !this.props.displayChangeCustomerLink
                                                                        ) {
                                                                            return false;
                                                                        }
                                                                        if (grandChild.type === "VmiUsersPage") {
                                                                            const currentMode =
                                                                                getCookie(NavigationModeCookieName);
                                                                            if (
                                                                                !currentMode ||
                                                                                currentMode === "Storefront"
                                                                            ) {
                                                                                return false;
                                                                            }
                                                                        }
                                                                        return true;
                                                                    })
                                                                    .map((grandGrandChild, index) => (
                                                                        <StyledWrapper
                                                                            // eslint-disable-next-line react/no-array-index-key
                                                                            key={index}
                                                                            className="level3-li"
                                                                            {...({ link, grandChild } as any)}
                                                                            css={css`
                                                                                ${({ link }: any) => {
                                                                                    if (
                                                                                        link &&
                                                                                        grandChild.children &&
                                                                                        grandChild.children.length > 0
                                                                                    ) {
                                                                                        if (
                                                                                            grandChild.children.length <
                                                                                            numberOfRowsPerColumn
                                                                                        ) {
                                                                                            return `width: 100%`;
                                                                                        }

                                                                                        return `
                                                                                            width: calc(
                                                                                                100% / ${Math.ceil(
                                                                                                    grandChild.children
                                                                                                        .length /
                                                                                                        numberOfRowsPerColumn,
                                                                                                )}
                                                                                            );
                                                                                        `;
                                                                                    }
                                                                                    return `
                                                                                            width: 100%;
                                                                                        `;
                                                                                }}
                                                                            `}
                                                                        >
                                                                            <Link
                                                                                className="mega-menu-link"
                                                                                {...styles.megaMenuLink}
                                                                                href={grandGrandChild.url}
                                                                                data-test-selector={
                                                                                    grandGrandChild.title
                                                                                }
                                                                                target={
                                                                                    grandGrandChild.openInNewWindow
                                                                                        ? "_blank"
                                                                                        : ""
                                                                                }
                                                                                onClick={() => {
                                                                                    // XNG-Change: track GA4
                                                                                    trackGaNavBarClickEvent(
                                                                                        grandGrandChild.title,
                                                                                        impersonation,
                                                                                    );
                                                                                    this.closePopover();
                                                                                }}
                                                                            >
                                                                                {parse(
                                                                                    grandGrandChild.title,
                                                                                    parserOptions,
                                                                                )}
                                                                            </Link>
                                                                        </StyledWrapper>
                                                                    ))}
                                                            </ul>
                                                        )}
                                                    </StyledWrapper>
                                                ))}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                </Tooltip>
            );
        }

        return menuItem;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(MainNavigationItem));
